import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { edgeErrorMessage } from "@/lib/edge-errors";

export interface GenerateCrmDraftInput {
  kind: "follow_up" | "reply";
  customerId: string;
  contactId?: string | null;
  followUpId?: string | null;
  /** Meldingen det svares på (påkrevd for `reply`). */
  threadActivityId?: string | null;
  instruction?: string | null;
  /** Auto-utkast: ingen feiltoast (brukeren ba ikke om utkastet selv). */
  silent?: boolean;
}

export interface CrmDraft {
  subject: string;
  body: string;
  model: string;
}

type DraftResponse = Partial<CrmDraft> & { error?: string; skipped?: string };

/**
 * Ber edge function generate-crm-draft om et utkast i Sølvrev-tone (CRM-2.8).
 * Lagrer ingenting: kallerens dialog viser utkastet redigerbart, og bare det
 * som sendes blir en aktivitet. Feilmeldingen kommer fra edge function.
 */
export function useGenerateCrmDraft() {
  return useMutation<CrmDraft, Error, GenerateCrmDraftInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.functions.invoke<DraftResponse>("generate-crm-draft", {
        body: {
          kind: input.kind,
          customer_id: input.customerId,
          contact_id: input.contactId ?? undefined,
          follow_up_id: input.followUpId ?? undefined,
          thread_activity_id: input.threadActivityId ?? undefined,
          instruction: input.instruction ?? undefined,
        },
      });
      if (error) throw new Error(await edgeErrorMessage(error, data, "Kunne ikke lage utkast"));
      if (data?.skipped === "flag_off") throw new Error("Mailutkast er ikke slått på ennå");
      if (data?.skipped === "agents_disabled") throw new Error("Mailutkast er slått av i dette miljøet");
      if (data?.error) throw new Error(data.error);
      if (!data?.subject || !data.body) throw new Error("Uventet svar fra utkasttjenesten");
      return { subject: data.subject, body: data.body, model: data.model ?? "" };
    },
    onError: (err, input) => {
      if (!input.silent) toast.error(err.message);
    },
  });
}
